import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import api, { getUser, isAuthed } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { CalendarClock, MapPin, Phone, Mail, KeyRound, Sparkles, Search, Heart, ChevronRight, Compass, ArrowUpRight, X, Trophy, Award, Lock, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroSearchBar from "@/components/HeroSearchBar";
import ReferralCard from "@/components/ReferralCard";
import BadgeShareButtons from "@/components/BadgeShareButtons";
import { API } from "@/lib/api";
import { TreasureHuntGame } from "@/pages/TreasureHunt";

function formatDate(iso, IT) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(IT ? "it-IT" : "en-US", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function statusChip(status, IT) {
  const s = (status || "").toLowerCase();
  const map = {
    paid:    { label: IT ? "Pagata" : "Paid",    cls: "bg-sage/15 text-sage border-sage/30" },
    pending: { label: IT ? "In attesa" : "Pending", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
    cancelled: { label: IT ? "Annullata" : "Cancelled", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  };
  const c = map[s] || { label: status || "—", cls: "bg-muted text-muted-foreground border-border" };
  return <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${c.cls}`}>{c.label}</span>;
}

export default function GuestDashboard() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [bookings, setBookings] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/guest/bookings").catch(() => ({ data: [] })),
      api.get("/wishlist/me").catch(() => ({ data: [] })),
    ]).then(([b, w]) => {
      setBookings(b.data || []);
      setWishlist(w.data || []);
    }).finally(() => setLoading(false));
  }, []);

  if (!isAuthed()) return <Navigate to="/guest/auth?mode=login" replace/>;
  const u = getUser();
  if (u?.role !== "guest") return <Navigate to="/dashboard" replace/>;

  const now = new Date();
  const upcoming = bookings.filter((b) => b.check_in && new Date(b.check_in) >= now);
  const past = bookings.filter((b) => !b.check_in || new Date(b.check_in) < now);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="guest-dashboard">
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">
          {IT ? "Area viaggiatori" : "Traveller area"}
        </div>
        <h1 className="font-display text-4xl mb-1" data-testid="guest-welcome">
          {IT ? `Ciao ${u?.name || ""}` : `Hi ${u?.name || ""}`}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {bookings.length > 0
            ? (IT ? `${bookings.length} prenotazione${bookings.length === 1 ? "" : "i"} nel tuo account.` : `${bookings.length} booking${bookings.length === 1 ? "" : "s"} on your account.`)
            : (IT ? "Nessuna prenotazione ancora — trova la tua prossima meta." : "No bookings yet — find your next stay.")}
        </p>

        {/* Vetrinetta dei Badge — hero placement on parchment. Renders only
            when the traveller has completed at least one hunt; empty state
            stays quiet. Single, unified showcase (removed from lower
            "Le mie mappe" block so the badges live in one canonical place). */}
        <BadgeVitrineTop IT={IT}/>

        {/* Treasure hunts — flagship experience, surfaced at the top so returning guests find something to do next */}
        <GuestTreasureHuntsBanner IT={IT}/>

        {/* Le mie mappe — separate section anchored at #mappe so the navbar
            link "Le mie mappe" scrolls straight to the traveller's own
            progress + badge showcase without touching the hunts explorer. */}
        <MyMapsSection IT={IT} guestName={u?.name}/>

        {/* Booking search bar */}
        <div className="mb-10" data-testid="guest-dashboard-search">
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2">
            {IT ? "Cerca la tua prossima struttura" : "Find your next stay"}
          </div>
          <HeroSearchBar/>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground animate-pulse">{IT ? "Caricamento…" : "Loading…"}</div>
        ) : (
          <>
            {/* Upcoming */}
            <Section title={IT ? "Prenotazioni future" : "Upcoming"} count={upcoming.length}>
              {upcoming.length === 0 ? (
                <EmptyState IT={IT} kind="upcoming"/>
              ) : (
                <div className="space-y-3">
                  {upcoming.map((b) => <BookingRow key={b.id} b={b} IT={IT}/>)}
                </div>
              )}
            </Section>

            {/* Past */}
            {past.length > 0 && (
              <Section title={IT ? "Prenotazioni passate" : "Past bookings"} count={past.length}>
                <div className="space-y-3 opacity-90">
                  {past.map((b) => <BookingRow key={b.id} b={b} IT={IT}/>)}
                </div>
              </Section>
            )}

            {/* Wishlist */}
            {wishlist.length > 0 && (
              <Section title={IT ? "Preferite" : "Favorites"} count={wishlist.length} icon={Heart}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wishlist-grid">
                  {wishlist.map((w) => <WishlistCard key={w.property.id} w={w} IT={IT}/>)}
                </div>
              </Section>
            )}

            {/* Referral */}
            <ReferralCard guestName={u?.name}/>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, icon: Icon, children }) {
  return (
    <section className="mb-10">
      <div className="flex items-baseline gap-3 mb-3">
        {Icon && <Icon className="w-5 h-5 text-primary self-center"/>}
        <h2 className="font-display text-2xl">{title}</h2>
        <span className="text-xs text-muted-foreground">· {count}</span>
      </div>
      {children}
    </section>
  );
}

function WishlistCard({ w, IT }) {
  const p = w.property;
  return (
    <Link to={`/p/${p.slug}`} className="group block border border-border rounded-lg bg-card overflow-hidden lift" data-testid={`wishlist-item-${p.slug}`}>
      <div className="relative aspect-[4/3] bg-muted">
        {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-primary text-white grid place-items-center shadow">
          <Heart className="w-4 h-4 fill-white"/>
        </div>
      </div>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{p.city}, {p.region} · {p.property_type}</div>
        <div className="font-display text-lg group-hover:text-primary transition-colors truncate">{p.name}</div>
        {p.from_price && <div className="text-xs text-muted-foreground mt-1">{IT ? "Da" : "From"} <span className="font-semibold text-foreground">€{p.from_price}</span>/{IT ? "notte" : "night"}</div>}
      </div>
    </Link>
  );
}

function EmptyState({ IT }) {
  return (
    <div className="border border-dashed border-border rounded-lg p-8 text-center">
      <Sparkles className="w-8 h-8 mx-auto text-primary/40 mb-2"/>
      <p className="text-sm text-muted-foreground mb-4">
        {IT ? "Non hai ancora prenotato. Esplora le strutture disponibili in tutta Italia." : "You haven't booked yet. Explore properties across Italy."}
      </p>
      <Link to="/strutture">
        <Button size="sm" className="lift" data-testid="guest-explore-btn"><Search className="w-4 h-4 mr-1.5"/>{IT ? "Esplora strutture" : "Explore properties"}</Button>
      </Link>
    </div>
  );
}

function BookingRow({ b, IT }) {
  const p = b.property || {};
  return (
    <Link
      to={`/guest/dashboard/booking/${b.id}`}
      className="border border-border rounded-lg bg-card overflow-hidden grid grid-cols-[120px_1fr_auto] sm:grid-cols-[180px_1fr_auto] hover:border-primary/40 transition-colors group"
      data-testid={`guest-booking-${b.id}`}
    >
      <div className="bg-muted min-h-[110px]">
        {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover"/>}
      </div>
      <div className="p-4 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.city || "—"}{p.region ? `, ${p.region}` : ""}</div>
        <div className="font-display text-xl truncate group-hover:text-primary transition-colors">{p.name || b.property_id}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {b.room_name && <span>{b.room_name} · </span>}
          {formatDate(b.check_in, IT)} → {formatDate(b.check_out, IT)}{" "}
          {b.nights ? <span>· {b.nights} {IT ? "notti" : "nights"}</span> : null}
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {statusChip(b.payment_status, IT)}
          {b.amount != null && <span className="text-xs text-muted-foreground">€{Number(b.amount).toFixed(0)}</span>}
        </div>
      </div>
      <div className="hidden sm:flex flex-col items-end justify-center p-4 border-l border-border/60 bg-sand/20 gap-1">
        <span className="text-[11px] uppercase tracking-widest text-primary group-hover:underline whitespace-nowrap flex items-center gap-1">
          {IT ? "Dettagli" : "Details"} <ChevronRight className="w-3 h-3"/>
        </span>
        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><KeyRound className="w-3 h-3"/>{IT ? "Accesso e regole" : "Access & rules"}</span>
      </div>
    </Link>
  );
}

function GuestTreasureHuntsBanner({ IT }) {
  const [hunts, setHunts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Modal state — keeping the treasure hunt as an overlay preserves the
  // booking flow underneath: search state, filters and scroll position all
  // survive because Guest Dashboard never unmounts.
  const [openHuntId, setOpenHuntId] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/hunts")
      .then((r) => setHunts(r.data || []))
      .catch(() => setHunts([]))
      .finally(() => setLoading(false));
  }, []);
  // Body scroll-lock while modal open; also close on ESC — but only when there
  // is no nested shadcn Dialog open (validate/prize/purchase). We register in
  // CAPTURE phase so we run BEFORE Radix's own escape handler (which flushes
  // synchronously in capture). That way we can observe [data-state="open"] on
  // any nested dialog and yield ESC to it.
  useEffect(() => {
    if (!openHuntId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      setOpenHuntId(null);
    };
    document.addEventListener("keydown", onKey, true);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey, true); };
  }, [openHuntId]);
  if (loading || !hunts.length) return null;
  const top = hunts.slice(0, 3);
  return (
    <section className="mb-10" data-testid="guest-hunts-banner">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2 flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5"/>{IT ? "Mappa del Tesoro" : "Treasure Hunts"}
          </div>
          <h2 className="font-display text-2xl">
            {IT ? "Il viaggio ricomincia a piedi" : "Adventures on foot"}
          </h2>
        </div>
        {hunts.length > top.length && (
          <button onClick={() => navigate("/caccia-al-tesoro")} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="guest-hunts-see-all">
            {IT ? `Vedi tutte le ${hunts.length}` : `See all ${hunts.length}`} <ArrowUpRight className="w-3 h-3"/>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {top.map((h) => (
          <button
            key={h.id}
            type="button"
            onClick={() => setOpenHuntId(h.id)}
            className="group text-left relative aspect-[4/5] overflow-hidden rounded-sm shadow-md lift"
            data-testid={`guest-hunt-card-${h.id}`}
          >
            <img
              src={h.hero_image}
              alt={h.title || ""}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.15) 30%, rgba(35,31,27,0.9) 100%)" }}/>
            <div className="absolute inset-x-0 bottom-0 p-5 text-white">
              <div className="text-[10px] uppercase tracking-[0.35em] opacity-80 mb-2">{h.city}</div>
              <div className="font-display italic text-2xl leading-tight">{h.title}</div>
              {h.subtitle && (
                <div className="text-sm opacity-85 italic mt-1 line-clamp-2">{h.subtitle}</div>
              )}
              {h.completed_this_month > 0 && (
                <div className="mt-2.5 inline-flex items-center gap-1.5 text-[10px] font-medium bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-2 py-0.5" data-testid={`guest-hunt-count-${h.id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
                  {IT
                    ? `${h.completed_this_month} viaggiatori questo mese`
                    : `${h.completed_this_month} travellers this month`}
                </div>
              )}
              <div className="text-xs mt-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all inline-flex items-center gap-1">
                {IT ? "Comincia il viaggio" : "Begin the journey"} <ArrowUpRight className="w-3 h-3"/>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Full-screen overlay — keeps the booking flow alive underneath. Simple
          fixed positioning (no shadcn Dialog) so we get full control over the
          "X" placement and don't inherit the shadcn max-width. */}
      {openHuntId && (
        <div
          className="fixed inset-0 z-[60] overflow-y-auto bg-background animate-slide-up"
          role="dialog"
          aria-modal="true"
          aria-label={IT ? "Mappa del tesoro" : "Treasure hunt"}
          data-testid="guest-hunt-overlay"
        >
          <button
            onClick={() => setOpenHuntId(null)}
            className="fixed top-4 right-4 z-[70] w-11 h-11 rounded-full bg-black/60 text-white hover:bg-black flex items-center justify-center shadow-lg backdrop-blur"
            aria-label={IT ? "Chiudi e torna alle prenotazioni" : "Close and return to bookings"}
            data-testid="guest-hunt-overlay-close"
          >
            <X className="w-5 h-5"/>
          </button>
          <TreasureHuntGame huntId={openHuntId} embedded/>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// MyMapsSection — traveller's own hunt progress + completed maps + badge
// showcase. Scrolls into view when the navbar "Le mie mappe" link is
// clicked (uses the #mappe anchor). Empty state stays quiet so the section
// doesn't clutter the dashboard for guests who never opened a hunt.
// -----------------------------------------------------------------------------
function MyMapsSection({ IT, guestName }) {
  const [inProgress, setInProgress] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/guest/hunts")
      .then((r) => {
        setInProgress(r.data?.in_progress || []);
        setCompleted(r.data?.completed || []);
      })
      .catch(() => { setInProgress([]); setCompleted([]); })
      .finally(() => setLoading(false));
    // Handle the #mappe hash on first mount so the scroll happens after paint
    if (window.location.hash === "#mappe") {
      setTimeout(() => document.getElementById("mappe")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, []);

  if (loading) return null;
  const nothing = inProgress.length === 0 && completed.length === 0;
  if (nothing) return null;  // stay quiet until the guest actually starts a hunt

  return (
    <section id="mappe" className="mb-12 scroll-mt-24" data-testid="guest-my-maps">
      <div className="flex items-baseline gap-3 mb-4">
        <Compass className="w-5 h-5 text-primary self-center"/>
        <h2 className="font-display text-2xl">{IT ? "Le mie mappe" : "My maps"}</h2>
        <span className="text-xs text-muted-foreground">
          · {inProgress.length + completed.length}
        </span>
      </div>

      {inProgress.length > 0 && (
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5"/> {IT ? "In corso" : "In progress"}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {inProgress.map((h) => <InProgressCard key={h.hunt_id} h={h} IT={IT}/>)}
          </div>
        </div>
      )}

      {completed.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5 text-primary"/> {IT ? "Mappe completate" : "Completed maps"}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {completed.map((h) => <CompletedCard key={h.hunt_id} h={h} IT={IT}/>)}
          </div>

          {/* Badge showcase moved to the top of the dashboard as the hero
              "Vetrinetta dei Badge" on parchment — see <BadgeVitrineTop/>. */}
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// BadgeVitrineTop — parchment hero section that lives at the very top of
// the traveller dashboard. Renders the chronological BadgeTimeline of every
// conquered badge. Silent when the guest hasn't completed any hunt yet.
// -----------------------------------------------------------------------------
function BadgeVitrineTop({ IT }) {
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/guest/hunts")
      .then((r) => setCompleted(r.data?.completed || []))
      .catch(() => setCompleted([]))
      .finally(() => setLoading(false));
  }, []);
  if (loading || completed.length === 0) return null;
  return (
    <section
      className="relative -mx-6 sm:-mx-8 lg:-mx-12 mb-10 px-6 sm:px-8 lg:px-12 py-10 rounded-md overflow-hidden border-2 border-primary/20"
      data-testid="guest-badge-vitrine-top"
      style={{
        // Parchment radial + subtle grain — matches the 1800s treasure map
        // aesthetic used on the public TreasureHunt page.
        background: "radial-gradient(ellipse at center, #f7edd4 0%, #eddcb0 100%)",
        boxShadow: "inset 0 0 60px rgba(122, 79, 34, 0.15)",
      }}
    >
      {/* Faux page-edge shadows top/bottom for the parchment feel */}
      <div className="absolute inset-x-0 top-0 h-6 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(122,79,34,0.15), transparent)" }}/>
      <div className="absolute inset-x-0 bottom-0 h-6 pointer-events-none" style={{ background: "linear-gradient(0deg, rgba(122,79,34,0.15), transparent)" }}/>
      <div className="flex items-center gap-3 mb-6" style={{ color: "#5b3d18" }}>
        <Award className="w-6 h-6"/>
        <h2 className="font-display italic text-3xl" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
          {IT ? "Vetrinetta dei Badge" : "Badge showcase"}
        </h2>
        <span className="text-xs italic ml-1">· {completed.length} {IT ? "conquistat" + (completed.length === 1 ? "o" : "i") : (completed.length === 1 ? "earned" : "earned")}</span>
      </div>
      <BadgeTimeline items={completed} IT={IT}/>
    </section>
  );
}

function InProgressCard({ h, IT }) {
  const pct = h.total_count > 0 ? Math.round((h.visited_count / h.total_count) * 100) : 0;
  return (
    <Link to={`/caccia-al-tesoro/${h.hunt_id}`} className="group border border-border rounded-lg overflow-hidden bg-card lift block" data-testid={`my-map-inprogress-${h.hunt_id}`}>
      <div className="relative aspect-[4/3] bg-muted overflow-hidden">
        {h.hero_image && <img src={h.hero_image} alt={h.hunt_title} className="tesoro-vintage w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.05) 40%, rgba(35,31,27,0.85) 100%)" }}/>
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">{h.city}</div>
          <div className="font-display italic text-lg leading-tight line-clamp-2">{h.hunt_title}</div>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>{h.visited_count} / {h.total_count} {IT ? "tappe" : "stops"}</span>
          <span className="font-semibold text-foreground">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }}/>
        </div>
        <div className="mt-2 text-[11px] text-primary group-hover:underline inline-flex items-center gap-1">
          {IT ? "Riprendi" : "Continue"} <ArrowUpRight className="w-3 h-3"/>
        </div>
      </div>
    </Link>
  );
}

function CompletedCard({ h, IT }) {
  return (
    <div className="border border-primary/30 rounded-lg overflow-hidden bg-primary/5" data-testid={`my-map-completed-${h.hunt_id}`}>
      <div className="relative aspect-[4/3] bg-muted overflow-hidden">
        {h.hero_image && <img src={h.hero_image} alt={h.hunt_title} className="tesoro-vintage w-full h-full object-cover"/>}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.05) 40%, rgba(35,31,27,0.85) 100%)" }}/>
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded-full flex items-center gap-1">
          <Trophy className="w-3 h-3"/> {IT ? "Completata" : "Completed"}
        </span>
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">{h.city}</div>
          <div className="font-display italic text-lg leading-tight line-clamp-2">{h.hunt_title}</div>
        </div>
      </div>
      <div className="p-3 space-y-1.5">
        {h.prize_title && <div className="text-xs italic text-muted-foreground">🏆 {h.prize_title}</div>}
        <Link to={`/caccia-al-tesoro/${h.hunt_id}`} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
          {IT ? "Rivedi la mappa" : "Revisit the map"} <ArrowUpRight className="w-3 h-3"/>
        </Link>
      </div>
    </div>
  );
}

function BadgeShowcaseTile({ h, IT }) {
  // The api client's base URL already ends with `/api`, and the backend
  // returns `badge_url` as `/api/hunts/...`. Strip the leading `/api` so we
  // don't double-prefix and end up with a broken `.../api/api/...` src.
  const badgeSrc = `${API}${(h.badge_url || "").replace(/^\/api/, "")}`;
  return (
    <div className="border-2 border-primary/40 rounded-md p-4 bg-sand/40 flex flex-col gap-3 shadow-sm" data-testid={`badge-showcase-${h.hunt_id}`}>
      <div className="aspect-square rounded-md overflow-hidden bg-white ring-4 ring-primary/20 shadow-inner">
        <img
          src={badgeSrc}
          alt={`Badge ${h.hunt_title}`}
          className="w-full h-full object-cover"
          loading="lazy"
          data-testid={`badge-img-${h.hunt_id}`}
        />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">{h.city}</div>
        <div className="font-display italic text-base leading-tight line-clamp-2">{h.hunt_title}</div>
        {h.completed_at && (
          <div className="text-[10px] text-muted-foreground italic mt-0.5">
            {IT ? "Conquistato il " : "Earned on "}
            {new Date(h.completed_at).toLocaleDateString(IT ? "it-IT" : "en-US", { day: "2-digit", month: "long", year: "numeric" })}
          </div>
        )}
      </div>
      <BadgeShareButtons
        badgeUrl={h.badge_url}
        city={h.city}
        huntTitle={h.hunt_title}
        shareUrl={h.share_url}
        huntId={h.hunt_id}
        IT={IT}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// BadgeTimeline — 1800s-style vertical chronology of conquered badges.
// Dashed sepia line on the left, circular node with the year per entry, and
// the existing badge tile rendered on the right. When two adjacent badges
// share the same year, only the first one shows the year label so the
// timeline reads like a real travelogue rather than a spreadsheet.
// -----------------------------------------------------------------------------
function BadgeTimeline({ items, IT }) {
  // Ensure descending order (most recent at the top) regardless of what the
  // backend already sorted — keeps the UI robust to future API changes.
  const sorted = [...items].sort((a, b) => {
    const ka = a.completed_at || "";
    const kb = b.completed_at || "";
    return kb.localeCompare(ka);
  });
  let lastYear = null;
  return (
    <div className="relative pl-10 sm:pl-14" data-testid="badge-timeline">
      {/* Vertical dashed line — anchored so it stops at the last node */}
      <div
        className="absolute top-4 bottom-4 left-3 sm:left-5 border-l-2 border-dashed border-primary/40"
        aria-hidden="true"
      />
      <div className="space-y-8">
        {sorted.map((h, idx) => {
          const dt = h.completed_at ? new Date(h.completed_at) : null;
          const year = dt ? dt.getFullYear() : "—";
          const showYear = year !== lastYear;
          lastYear = year;
          return (
            <div key={h.hunt_id} className="relative" data-testid={`timeline-item-${h.hunt_id}`}>
              {/* Circular node on the timeline. Shows the year on the first
                  entry of that year; subsequent entries in the same year get
                  a small dot instead. */}
              <div
                className={`absolute -left-10 sm:-left-14 top-2 flex items-center justify-center rounded-full ring-4 ring-background shadow ${showYear ? "w-11 h-11 sm:w-14 sm:h-14 bg-primary text-primary-foreground font-display italic" : "w-4 h-4 sm:w-5 sm:h-5 bg-primary/70"}`}
                style={showYear ? { fontFamily: "'IM Fell English', Georgia, serif" } : undefined}
                aria-label={String(year)}
                data-testid={`timeline-node-${h.hunt_id}`}
              >
                {showYear ? <span className="text-sm sm:text-base leading-none">{year}</span> : null}
              </div>
              {dt && showYear && idx === 0 && (
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground italic mb-2">
                  {IT ? "L'ultima carovana" : "The latest chapter"}
                </div>
              )}
              {dt && (
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground italic mb-2">
                  {dt.toLocaleDateString(IT ? "it-IT" : "en-US", { day: "2-digit", month: "long" })}
                </div>
              )}
              <div className="max-w-md">
                <BadgeShowcaseTile h={h} IT={IT}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
